import {
	Tuple,
	Tuple2,
}	from "ts-base/tuple";
import { Fn, Fn2 }		from "ts-base/fn";
import { Is }			from "ts-base/is";
import { Predicate }	from "ts-base/predicate";
import { Nullable }		from "ts-base/nullable";
import { Arrays }		from "ts-base/arrays";
import { Nea }			from "ts-base/nea";
import { Maybe }		from "ts-base/maybe";
import { Either }		from "ts-base/either";
import { Sync }			from "ts-base/sync";
import { Async }		from "ts-base/async";
import { Zio }			from "ts-base/zio";

// exhaustive type parameters are necessary to infer the value
export type Validated<E, T>	= Validated.Invalid<E, T> | Validated.Valid<E, T>;

export namespace Validated {
	//------------------------------------------------------------------------------
	//## basics

	export type Value<T>	= T extends Validated<unknown, infer U> ? U : never;
	export type Error<T>	= T extends Validated<infer U, unknown> ? U : never;

	export type Valid<_E, T>	= { tag:"Valid",	value:T			};
	export type Invalid<E, _T>	= { tag:"Invalid",	errors:Nea<E>	};

	export const valid = <T>(value:T):Validated<never, T> => ({
		tag:	"Valid",
		value:	value,
	});

	export const invalid = <E>(errors:Nea<E>):Validated<E, never> => ({
		tag:	"Invalid",
		errors:	errors,
	});

	export const isValid	= <E, T>(self:Validated<E, T>):self is Valid<E, T>		=> self.tag === "Valid";
	export const isInvalid	= <E, T>(self:Validated<E, T>):self is Invalid<E, T>	=> self.tag === "Invalid";

	export const cata	= <E, T>(self:Validated<E, T>) => <EE, TT>(invalid:Fn<Nea<E>, EE>, valid:Fn<T, TT>):EE|TT	=>
		isValid(self) ? valid(self.value) : invalid(self.errors);

	export type Visitor<E, T, EE, TT>	= {
		valid:		Fn<T, TT>,
		invalid:	Fn<Nea<E>, EE>,
	};

	export const visit	= <E, T>(self:Validated<E, T>) => <EE, TT>(visitor:Visitor<E, T, EE, TT>):EE|TT	=>
		cata(self)(visitor.invalid, visitor.valid);

	//------------------------------------------------------------------------------
	//## construction

	export const invalid1 = <E, T>(error:E):Validated<E, T> => invalid([ error ]);

	export const tryCatch = <T>(value:()=>T):Validated<unknown, T> => {
		try { return pure(value()); }
		catch (e) { return invalid1(e); }
	};

	export const tryCatchWith = <T>(value:()=>T) => <E>(error:Fn<unknown, E>):Validated<E, T> =>
		mapErrors(tryCatch(value))(error);

	export const fromErrors	= <E>(errors:Nea<E>):Validated<E, void>	=>
		errors.length !== 0 ? invalid(errors) : valid(void(0));

	export const unlessErrors	= <E>(errors:ReadonlyArray<E>) => <T>(value:T):Validated<E, T>	=>
		errors.length !== 0 ? invalid(Nea.unsafeFromArray(errors)) : valid(value);

	//------------------------------------------------------------------------------
	//## conversion

	export const fromNullable = <T>(value:T|null) => <E>(error:E):Validated<E, T> =>
		value !== null ? pure(value) : invalid1(error);

	export const toNullable = <E, T>(self:Validated<E, T>):T|null =>
		isValid(self) ? self.value : null;

	export const fromMaybe	= <T>(value:Maybe<T>) => <E>(error:E):Validated<E, T>	=>
		Maybe.isSome(value) ? valid(value.value) : invalid1(error);

	export const toMaybe	= <E, T>(self:Validated<E, T>):Maybe<T>	=>
		isValid(self) ? Maybe.some(self.value) : Maybe.none();

	export const toArray	= <E, T>(self:Validated<E, T>):Array<T>	=>
		isValid(self) ? [ self.value ] : [];

	//------------------------------------------------------------------------------
	//## tests

	export const includes	= <E, T>(self:Validated<E, T>) => (value:T):boolean	=>
		exists(self)(it => it === value);

	export const exists	= <E, T>(self:Validated<E, T>) => (pred:Predicate<T>):boolean =>
		isValid(self) ? pred(self.value) : false;

	export const forall	= <E, T>(self:Validated<E, T>) => (pred:Predicate<T>):boolean =>
		isValid(self) ? pred(self.value) : true;

	export const some	= exists;
	export const every	= forall;

	//------------------------------------------------------------------------------
	//## errors

	export const fail	= invalid;

	export const mapErrors = <E, T>(self:Validated<E, T>) => <F>(func:Fn<E, F>):Validated<F, T> =>
		isInvalid(self) ? invalid(Nea.map(self.errors)(func)) : self;

	export const errorsToNullable	= <E, T>(self:Validated<E, T>):Nea<E>|null =>
		isInvalid(self) ? self.errors : null;

	export const unsafeGetErrors	= <E, T>(self:Validated<E, T>):Nea<E> => {
		if (isInvalid(self))	return self.errors;
		else					throw new Error(`unexpected Valid: ${String(self.value)}`);
	};

	// TODO rescue combinators

	//------------------------------------------------------------------------------
	//## sideeffect

	export const forEach	= <E, T>(self:Validated<E, T>)	=> (handler:Fn<T, void>):void	=> {
		if (isValid(self)) {
			handler(self.value);
		}
	};

	//------------------------------------------------------------------------------
	//## alternative

	export const get	= <E, T>(self:Validated<E, T>) => <U>(alternative:U):T|U	=>
		isValid(self) ? self.value : alternative;

	/** danger! */
	export const unsafeGet	= <E, T>(self:Validated<E, T>):T	=> {
		if (isValid(self))	return self.value;
		else				throw new Error("unexpected Invalid: " + self.errors.join(", "));
	};

	/** danger! */
	export const orThrowWith	= <E, T>(self:Validated<E, T>) => <X>(handler:Fn<Nea<E>, X>):T	=> {
		if (isValid(self))	return self.value;
		// eslint-disable-next-line @typescript-eslint/only-throw-error
		else				throw handler(self.errors);
	};

	export const orElse	= <E, T>(a:Validated<E, T>) => (b:Validated<E, T>):Validated<E, T> =>
		isInvalid(a) && isInvalid(b) ? invalid(Nea.concat(a.errors, b.errors)) : isValid(a) ? a : b;

	export const filterIs	= <E, S>(self:Validated<E, S>) => <T extends S>(is:Is<S, T>) => (errors:Nea<E>):Validated<E, T>	=>
		isInvalid(self) ? self : is(self.value) ? valid(self.value) : invalid(errors);

	export const filterIs1	= <E, S>(self:Validated<E, S>) => <T extends S>(is:Is<S, T>) => (error:E):Validated<E, T>	=>
		filterIs(self)(is)([ error ]);

	//------------------------------------------------------------------------------
	//## functor

	export const map = <E, T>(self:Validated<E, T>) => <U>(func:Fn<T, U>):Validated<E, U> =>
		isValid(self) ? valid(func(self.value)) : self;

	export const tupleBy	= <E, T>(self:Validated<E, T>) => <U>(func:Fn<T, U>):Validated<E, Tuple2<T, U>>	=>
		map(self)(it => [ it, func(it) ]);

	//------------------------------------------------------------------------------
	//## applicative functor

	export const pure	= valid;

	// NOTE puts function errors in from of value errors
	export const ap	= <E, S>(self:Validated<E, S>) => <T>(func:Validated<E, Fn<S, T>>):Validated<E, T>	=>
		map2(func, self)((f, v) => f(v));

	export const map2 = <E, S1, S2>(s1:Validated<E, S1>, s2:Validated<E, S2>) => <T>(func:Fn2<S1, S2, T>):Validated<E, T> =>
		isInvalid(s1) && isInvalid(s2)	? invalid(Nea.concat(s1.errors, s2.errors)) :
		isInvalid(s1)					? s1 :
		isInvalid(s2)					? s2 :
		valid(func(s1.value, s2.value));

	export const tuple2	= <E, T1, T2>(t1:Validated<E, T1>, t2:Validated<E, T2>):Validated<E, Tuple2<T1, T2>>	=>
		map2(t1, t2)(Tuple.ofN);

	export const tupleN	= <E, T1, T extends ReadonlyArray<Validated<E, unknown>>>(arg:Validated<E, T1>, ...args:T):Validated<E, Tuple.Prepend<T1, ValueN<T>>>	=>
		mapN(arg, ...args)(Tuple.ofN);

	export const mapN	= <E, S1, S extends ReadonlyArray<Validated<E, unknown>>>(arg:Validated<E, S1>, ...args:S) => <T>(func:(arg:S1, ...args:ValueN<S>)=>T):Validated<E, T>	=> {
		const values:Array<unknown>	= [];
		const errors:Array<E>		= [];
		const items:ReadonlyArray<Validated<E, unknown>>	= [ arg, ...args ];
		for (const item of items) {
			if (isValid(item))	values.push(item.value);
			else				Arrays.pushAll(errors)(item.errors);
		}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return unlessErrors(errors)((func as any)(...(values as [ unknown ])));
	};

	type ValueN<T>	= { [P in keyof T]:Value<T[P]> };

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	type AnySelfRecord	= Record<string, Validated<any, any>>;

	export type SelfRecord<E, T>	= {
		[K in keyof T]: Validated<E, T[K]>
	};

	export type ValueRecord<T>	= {
		[K in keyof T]: Value<T[K]>
	};

	export type ErrorRecord<T>	= {
		[K in keyof T]: Error<T[K]>
	}[keyof T];

	// this can both reject ill-typed input fields and infer the union of error types
	export const object	= <const T extends AnySelfRecord>(input:T):Validated<ErrorRecord<T>, ValueRecord<T>>	=> {
		const errors:Array<ErrorRecord<T>>	= [];
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const out:any	= {};
		for (const key in input) {
			const part	= input[key];
			if (isValid(part))	out[key]	= part.value;
			else							Arrays.pushAll(errors)(part.errors);
		}
		return unlessErrors(errors)(out);
	};

	//------------------------------------------------------------------------------
	//## pseudo monad

	/** almost an ap, but incompatible with the error-merging behaviour of our prefered applicative functor */
	export const thenAp	= <E, S>(self:Validated<E, S>) => <T>(func:Validated<E, Fn<S, T>>):Validated<E, T> =>
		isInvalid(self) ? self : isInvalid(func) ? func : valid(func.value(self.value));

	/** almost a flatMap, but as a monad operation it would be inconsistent with ap */
	export const then = <E, T>(self:Validated<E, T>) => <U>(func:Fn<T, Validated<E, U>>):Validated<E, U> =>
		isValid(self) ? func(self.value) : self;

	/** almost a flatten, but as a monad operation it would be inconsistent with ap */
	export const thenFlat = <E, T>(self:Validated<E, Validated<E, T>>):Validated<E, T> =>
		then(self)(Fn.identity);

	export const pureKleisli	= <E, S, T>(func:Fn<S, T>):Fn<S, Validated<E, T>>	=>
		Fn.andThen(func, it => pure(it));

	export const predicateKleisli = <E, T>(test:Predicate<T>, message:E):Fn<T, Validated<E, T>> =>
		input => test(input) ? pure(input) : invalid1(message);

	export const functionKleisli	= <E, S, T>(func:Validated<E, Fn<S, T>>):Fn<S, Validated<E, T>>	=>
		input => map(func)(func => func(input));

	export const andThen = <E, R, S, T>(a:Fn<R, Validated<E, S>>, b:Fn<S, Validated<E, T>>):Fn<R, Validated<E, T>> =>
		it => then(a(it))(b);

	export const andThen3 = <E, R, S, T, U>(a:Fn<R, Validated<E, S>>, b:Fn<S, Validated<E, T>>, c:Fn<T, Validated<E, U>>):Fn<R, Validated<E, U>> =>
		andThen(a, andThen(b, c));

	export const andThen4 = <E, R, S, T, U, V>(a:Fn<R, Validated<E, S>>, b:Fn<S, Validated<E, T>>, c:Fn<T, Validated<E, U>>, d:Fn<U, Validated<E, V>>):Fn<R, Validated<E, V>> =>
		andThen(a, andThen(b, andThen(c, d)));

	export const mapKleisli	= <E, S, T>(base:Fn<S, Validated<E, T>>) => <U>(func:Fn<T, U>):Fn<S, Validated<E, U>>	=>
		andThen(base, pureKleisli(func));

	export const contraMapKleisli	= <E, S, T>(base:Fn<S, Validated<E, T>>) => <R>(func:Fn<R, S>):Fn<R, Validated<E, T>>	=>
		andThen(pureKleisli(func), base);

	//-----------------------------------------------------------------------------
	//## traversal

	export const traverseKleisli	= <E, S>(self:Validated<E, S>) => <C, T>(func:Fn<S, Fn<C, T>>):Fn<C, Validated<E, T>>	=>
		(t:C) => isValid(self) ? valid(func(self.value)(t)) : self;

	export const sequenceKleisli	= <C, E, T>(self:Validated<E, Fn<C, T>>):Fn<C, Validated<E, T>>	=>
		traverseKleisli(self)(Fn.identity);

	export const traverseNullable	= <E, S>(self:Validated<E, S>) => <T>(func:Fn<S, Nullable<T>>):Nullable<Validated<E, T>>	=>
		isValid(self) ? Nullable.then(func(self.value))(it => valid(it)) : self;

	export const sequenceNullable	= <E, T>(self:Validated<E, T|null>):Validated<E, T>|null	=>
		traverseNullable(self)(Fn.identity);

	export const traverseArray	= <E, S>(self:Validated<E, S>) => <T>(func:Fn<S, ReadonlyArray<T>>):Array<Validated<E, T>>	=>
		isValid(self) ? func(self.value).map(it => valid(it)) : [ self ];

	export const sequenceArray	= <E, T>(self:Validated<E, Array<T>>):Array<Validated<E, T>>	=>
		traverseArray(self)(Fn.identity);

	export const traverseNea	= <E, S>(self:Validated<E, S>) => <T>(func:Fn<S, Nea<T>>):Nea<Validated<E, T>>	=>
		isValid(self) ? Nea.map(func(self.value))(it => valid(it)) : Nea.single(self);

	export const sequenceNea	= <E, T>(self:Validated<E, Nea<T>>):Nea<Validated<E, T>>	=>
		traverseNea(self)(Fn.identity);

	export const traverseMaybe	= <E, S>(self:Validated<E, S>) => <T>(func:Fn<S, Maybe<T>>):Maybe<Validated<E, T>>	=>
		isValid(self) ? Maybe.map(func(self.value))(it => valid(it)) : Maybe.some(self);

	export const sequenceMaybe	= <E, T>(self:Validated<E, Maybe<T>>):Maybe<Validated<E, T>>	=>
		traverseMaybe(self)(Fn.identity);

	export const traverseEither	= <E, S>(self:Validated<E, S>) => <F, T>(func:Fn<S, Either<F, T>>):Either<F, Validated<E, T>>	=>
		isValid(self) ? Either.map(func(self.value))(it => valid(it)) : Either.right(self);

	export const sequenceEither	= <E, F, T>(self:Validated<E, Either<F, T>>):Either<F, Validated<E, T>>	=>
		traverseEither(self)(Fn.identity);

	export const traverseValidated	= <E, S>(self:Validated<E, S>) => <F, T>(func:Fn<S, Validated<F, T>>):Validated<F, Validated<E, T>>	=>
		isValid(self) ? map(func(self.value))(it => valid(it)) : valid(self);

	export const sequenceValidated	= <E, F, T>(self:Validated<E, Validated<F, T>>):Validated<F, Validated<E, T>>	=>
		traverseValidated(self)(Fn.identity);

	export const traversePromise	= <E, S>(self:Validated<E, S>) => <T>(func:Fn<S, Promise<T>>):Promise<Validated<E, T>>	=>
		isValid(self) ? func(self.value).then(it => valid(it)) : Promise.resolve(self);

	export const sequencePromise	= <E, T>(self:Validated<E, Promise<T>>):Promise<Validated<E, T>>	=>
		traversePromise(self)(Fn.identity);

	export const traverseSync	= <E, S>(self:Validated<E, S>) => <T>(func:Fn<S, Sync<T>>):Sync<Validated<E, T>>	=>
		() => isValid(self) ? valid(func(self.value)()) : self;

	export const sequenceSync	= <E, T>(self:Validated<E, Sync<T>>):Sync<Validated<E, T>>	=>
		traverseSync(self)(Fn.identity);

	export const traverseAsync	= <E, S>(self:Validated<E, S>) => <T>(func:Fn<S, Async<T>>):Async<Validated<E, T>>	=>
		()	=> traversePromise(self)(it => func(it)());

	export const sequenceAsync	= <E, T>(self:Validated<E, Async<T>>):Async<Validated<E, T>>	=>
		traverseAsync(self)(Fn.identity);

	export const traverseZio	= <E, S>(self:Validated<E, S>) => <C, T>(func:Fn<S, Zio<C, T>>):Zio<C, Validated<E, T>>	=>
		(c:C) => traversePromise(self)((it) => func(it)(c));

	export const sequenceZio	= <E, C, T>(self:Validated<E, Zio<C, T>>):Zio<C, Validated<E, T>>	=>
		traverseZio(self)(Fn.identity);
}
